import { useEffect, useState } from "react";
import { Modal, DialogFooter, Loader } from "@/ui/atoms";
import { FEATURE_FLAG_DEFINITIONS, overridesFromEffective } from "@/utils/featureFlagDefinitions";

/** What the modal edits: the user's effective flags against the company's. */
export type TUserFeatureFlagsData = {
    overrides: Record<string, boolean>;
    companyFlags: Record<string, boolean>;
    effectiveFlags: Record<string, boolean>;
};

export type TFeatureFlagsModalProps = {
    open: boolean;
    onClose: () => void;
    /** Who the flags are for, e.g. "Jane Doe" or "you". */
    subjectLabel: string;
    /** Null while loading. */
    data: TUserFeatureFlagsData | null;
    loadError?: string | null;
    /** Receives per-flag overrides: a boolean to set, null to follow the company. */
    onSave: (overrides: Record<string, boolean | null>) => Promise<void>;
};

/**
 * Per-user feature flags (Proda users only). Each checkbox shows the flag as
 * it applies to the user; a value that differs from the company's is stored as
 * an override and marked with a tag, a value equal to the company's clears it.
 */
export function FeatureFlagsModal({
    open,
    onClose,
    subjectLabel,
    data,
    loadError = null,
    onSave,
}: TFeatureFlagsModalProps) {
    const [effective, setEffective] = useState<Record<string, boolean>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        if (data) setEffective({ ...data.effectiveFlags });
        setSaveError(null);
    }, [data]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!data) return;
        setIsSaving(true);
        setSaveError(null);
        try {
            await onSave(overridesFromEffective(effective, data.companyFlags));
            onClose();
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : "Failed to save feature flags");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onOpenChange={(isOpen) => {
                if (!isOpen) onClose();
            }}
            title={`Feature flags for ${subjectLabel}`}
            description="Each flag follows the company unless set differently here. An override follows the user everywhere, including while viewing as another company. A tag marks flags that differ from the company's setting."
        >
            {loadError ? (
                <p className="text-sm text-destructive" data-testid="user-flags-error">
                    {loadError}
                </p>
            ) : !data ? (
                <div className="py-6 flex justify-center">
                    <Loader size="sm" />
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-flags-form">
                    {FEATURE_FLAG_DEFINITIONS.map(({ key, label, description }) => {
                        const value = effective[key] ?? false;
                        const companyValue = data.companyFlags[key] ?? false;
                        const differs = value !== companyValue;
                        return (
                            <label key={key} className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    className="mt-1"
                                    checked={value}
                                    onChange={(e) =>
                                        setEffective((prev) => ({
                                            ...prev,
                                            [key]: e.target.checked,
                                        }))
                                    }
                                    disabled={isSaving}
                                    data-testid={`user-flag-${key}`}
                                />
                                <span className="min-w-0">
                                    <span className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium">{label}</span>
                                        {differs && (
                                            <span
                                                className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
                                                title={`The company has this ${companyValue ? "on" : "off"}`}
                                                data-testid={`user-flag-${key}-differs`}
                                            >
                                                Differs from company ({companyValue ? "on" : "off"})
                                            </span>
                                        )}
                                    </span>
                                    <span className="block text-xs text-muted-foreground">
                                        {description}
                                    </span>
                                </span>
                            </label>
                        );
                    })}

                    {saveError && <p className="text-sm text-destructive">{saveError}</p>}

                    <DialogFooter className="gap-2 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium border rounded-lg hover:bg-muted transition-colors"
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                            disabled={isSaving}
                            data-testid="user-flags-save"
                        >
                            {isSaving ? (
                                <>
                                    <Loader size="sm" className="inline mr-2" />
                                    Saving...
                                </>
                            ) : (
                                "Save flags"
                            )}
                        </button>
                    </DialogFooter>
                </form>
            )}
        </Modal>
    );
}
